import { Injectable } from '@angular/core';

import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { UtilsHelper } from '@helpers/utils-helper';
import { ValidationMessageModel } from '@data-layer/models/common';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { CURRENCY_IS_NOT_AVAILABLE } from '@constants/entity-validation-messages';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class EsValidationsHelperService {
  public getValidationMessages(
    allValidationMessages: ValidationMessageModel[],
    invoice: InvoiceModel,
    transportationMode: enumTransportationMode,
  ): ValidationMessageModel[] {
    let validationMessages: ValidationMessageModel[] = [];
    if (!invoice) {
      return;
    }
    validationMessages = UtilsHelper.concat<ValidationMessageModel>(
      validationMessages,
      EntrySummaryValidationHelper.getValidationMessages(invoice, transportationMode, true),
    );
    validationMessages = UtilsHelper.uniqBy<ValidationMessageModel>(validationMessages, 'text');

    allValidationMessages.forEach((message: ValidationMessageModel) => {
      if (message.text === CURRENCY_IS_NOT_AVAILABLE && message.params.invoiceId === invoice.id) {
        validationMessages.push(message);
      }
    });

    return validationMessages;
  }

  public getManufactureValidationMessages(invoice: InvoiceModel, transportationMode: enumTransportationMode): ValidationMessageModel[] {
    let manufacturerValidationMessages: ValidationMessageModel[] = [];
    if (!invoice) {
      return;
    }

    manufacturerValidationMessages = UtilsHelper.concat<ValidationMessageModel>(
      manufacturerValidationMessages,
      EntrySummaryValidationHelper.getValidationMessages(invoice.manufacturer, transportationMode),
    );

    if (invoice?.manufacturer?.address?.validationMessages) {
      // 5034 filter out existing address messages
      const addressKey: string = 'ADDRESS.';
      const existingAddressTexts: string[] = Object.values(invoice.manufacturer.address.validationMessages).map(
        (message: any) => message?.text,
      );
      manufacturerValidationMessages = manufacturerValidationMessages.filter(
        (message: any) => !existingAddressTexts.includes(message.text),
      );
      const addressMessages: ValidationMessageModel[] = Object.values(invoice.manufacturer.address.validationMessages).map(
        (message: any) => ({ ...message, text: addressKey + message.text }),
      );

      manufacturerValidationMessages.push(...addressMessages);
    }

    manufacturerValidationMessages = UtilsHelper.uniqBy<ValidationMessageModel>(manufacturerValidationMessages, 'text');

    return manufacturerValidationMessages;
  }

  public getLinesValidationMessages(invoice: InvoiceModel, transportationMode: enumTransportationMode): ValidationMessageModel[] {
    let linesValidationMessages: ValidationMessageModel[] = [];
    if (!invoice) {
      return;
    }

    for (const line of invoice.lines) {
      linesValidationMessages = UtilsHelper.concat<ValidationMessageModel>(
        linesValidationMessages,
        EntrySummaryValidationHelper.getValidationMessages(line, transportationMode),
      );
    }
    linesValidationMessages = UtilsHelper.uniqBy<ValidationMessageModel>(linesValidationMessages, 'text');

    return linesValidationMessages;
  }

  public getEsSummaryModalModifiedData(transportationMode: enumTransportationMode, data: any): any {
    const modifiedData: any = cloneDeep(data);
    if (transportationMode === enumTransportationMode.AIR && modifiedData.carrier) {
      modifiedData.airlinePrefix = 'airlinePrefix';
      delete modifiedData.carrier;
    }
    if (transportationMode === enumTransportationMode.RAIL && modifiedData.carrier) {
      modifiedData.railPrefix = 'railPrefix';
      delete modifiedData.carrier;
    }
    if (modifiedData.number) {
      if (transportationMode === enumTransportationMode.OCEAN) {
        modifiedData.billNumber = modifiedData.number;
      } else if (transportationMode === enumTransportationMode.AIR) {
        modifiedData.airBillNumber = modifiedData.number;
      } else if (transportationMode === enumTransportationMode.RAIL) {
        modifiedData.railBillNumber = modifiedData.number;
      } else if (transportationMode === enumTransportationMode.TRUCK) {
        modifiedData.truckBillNumber = modifiedData.number;
      }
      delete modifiedData.number;
    }
    return modifiedData;
  }
}
